import { useState, useEffect } from 'react'
import {
  useProduct,
  useUpdateProduct,
  useUpdateProductPricing,
  useDeleteProductPricing,
} from '@/hooks/use-api'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { useToast } from '@/hooks/use-toast'
import { usePicklistOptions } from '@/hooks/use-picklists'
import type { Product } from '@/types'
import { explainError } from '@/lib/explain-error'

interface EditProductModalProps {
  productId: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

interface ProductFormData extends Omit<Partial<Product>, 'unit_price'> {
  unit_price?: string
  /**
   * Currency of the PRICE, which is not the same thing as the product record's
   * own `currency_iso_code`. The displayed price comes from a pricebook entry
   * (`pricing_currency`); the product's field is separate metadata and the two
   * disagree for most products. Conflating them silently rewrote USD prices as
   * CAD — see the comment in handleSubmit.
   */
  pricing_currency?: string
}

export function EditProductModal({ productId, open, onOpenChange }: EditProductModalProps) {
  const { toast } = useToast()
  const { data: productData, isLoading: isLoadingProduct } = useProduct(productId || '')
  const updateMutation = useUpdateProduct()
  const updatePricingMutation = useUpdateProductPricing()
  const deletePricingMutation = useDeleteProductPricing()
  const currencyCodes = usePicklistOptions('general', 'currency_code')

  // useProduct already resolves to the unwrapped Product (apiClient.getProduct
  // returns data.data), so use it directly — an extra `.data` here was always
  // undefined and showed "Product not found" for every product.
  const product = productData

  const [formData, setFormData] = useState<ProductFormData>({
    name: '',
    product_code: '',
    description: '',
    is_active: true,
    renewal: false,
    family: '',
    currency_iso_code: 'USD',
    stock_keeping_unit: '',
    quantity_unit_of_measure: '',
    unit_price: '',
    pricing_currency: 'USD',
  })

  const [errors, setErrors] = useState<Record<string, string>>({})

  // The price/currency the form loaded with, so we can tell whether the user
  // actually touched pricing. Without this the modal re-wrote the pricebook
  // entry on every save, including saves that only changed the description.
  const [initialPricing, setInitialPricing] = useState<{
    unit_price: string
    pricing_currency: string
  }>({ unit_price: '', pricing_currency: 'USD' })

  // Populate form when product data loads
  useEffect(() => {
    if (product) {
      // The price shown belongs to the pricebook entry it was read from, so the
      // currency must come from `pricing_currency` — NOT the product's own
      // `currency_iso_code`. Fall back to the product's currency only when the
      // product has no price yet and we're about to create the first entry.
      const priceCurrency = product.pricing_currency || product.currency_iso_code || 'USD'
      const price = product.unit_price?.toString() || ''

      setFormData({
        name: product.name || '',
        product_code: product.product_code || '',
        description: product.description || '',
        is_active: product.is_active ?? true,
        renewal: product.renewal ?? false,
        family: product.family || '',
        currency_iso_code: product.currency_iso_code || 'USD',
        stock_keeping_unit: product.stock_keeping_unit || '',
        quantity_unit_of_measure: product.quantity_unit_of_measure || '',
        unit_price: price,
        pricing_currency: priceCurrency,
      })
      setInitialPricing({ unit_price: price, pricing_currency: priceCurrency })
    }
  }, [product])

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.name?.trim()) {
      newErrors.name = 'Product name is required'
    }

    if (formData.unit_price && formData.unit_price.trim()) {
      const price = parseFloat(formData.unit_price)
      if (isNaN(price) || price < 0) {
        newErrors.unit_price = 'Unit price must be a valid non-negative number'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validate() || !productId) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields correctly.',
        variant: 'destructive',
      })
      return
    }

    try {
      // Pricing lives in sales.pricebook_entries, not on the product row, so
      // strip both pricing fields out of the product update payload.
      const { unit_price, pricing_currency, ...productData } = formData

      // Update the product
      await updateMutation.mutateAsync({ id: productId, data: productData })

      // Handle pricing updates
      const hadPricing = product?.has_pricing && product.unit_price !== null
      const hasPricingNow = unit_price && unit_price.trim()

      // Only touch pricing when the user actually changed it.
      //
      // This guard is the whole point of the fix. Previously any save with a
      // non-empty price field re-wrote the pricebook entry using the PRODUCT's
      // currency. Since the displayed price is whichever entry the API's
      // currency ladder picked (USD for 67 of 73 products) while the product's
      // own currency is frequently CAD, editing a description would store the
      // USD figure as CAD — a silent ~31% price cut on 27 products.
      const pricingChanged =
        (unit_price || '') !== initialPricing.unit_price ||
        (pricing_currency || 'USD') !== initialPricing.pricing_currency

      if (hasPricingNow && pricingChanged) {
        // Update or create pricing
        try {
          await updatePricingMutation.mutateAsync({
            id: productId,
            unit_price: parseFloat(unit_price),
            // The price's own currency — never the product record's.
            currency_iso_code: pricing_currency || 'USD',
          })
        } catch (pricingError) {
          toast({
            title: 'Partial Success',
            description: 'Product updated, but failed to update pricing.',
            variant: 'default',
          })
          return
        }
      } else if (hadPricing && !hasPricingNow) {
        // User cleared the price, delete pricing
        try {
          await deletePricingMutation.mutateAsync(productId)
        } catch (pricingError) {
          toast({
            title: 'Partial Success',
            description: 'Product updated, but failed to remove pricing.',
            variant: 'default',
          })
          return
        }
      }

      toast({
        title: 'Success',
        description: 'Product updated successfully.',
      })

      onOpenChange(false)
    } catch (error) {
      const errorMessage = explainError(error, 'Failed to update product')
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  const handleChange = (field: keyof typeof formData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  if (!open) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        aria-describedby="edit-product-description"
      >
        <DialogHeader>
          <DialogTitle>Edit Product</DialogTitle>
          <DialogDescription id="edit-product-description">
            Update product details and pricing information. All fields marked with * are required.
          </DialogDescription>
        </DialogHeader>

        {isLoadingProduct ? (
          <div className="text-center py-8 text-muted-foreground">Loading product details...</div>
        ) : !product ? (
          <div className="text-center py-8 text-destructive">Product not found</div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Product Name */}
            <div>
              <Label htmlFor="name" className="required">
                Product Name *
              </Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                aria-required="true"
                aria-invalid={!!errors.name}
                aria-describedby={errors.name ? 'name-error' : undefined}
              />
              {errors.name && (
                <p id="name-error" className="text-sm text-destructive mt-1" role="alert">
                  {errors.name}
                </p>
              )}
            </div>

            {/* Product Code */}
            <div>
              <Label htmlFor="product_code">Product Code</Label>
              <Input
                id="product_code"
                value={formData.product_code}
                onChange={(e) => handleChange('product_code', e.target.value)}
              />
            </div>

            {/* Family */}
            <div>
              <Label htmlFor="family">Family</Label>
              <Input
                id="family"
                value={formData.family}
                onChange={(e) => handleChange('family', e.target.value)}
              />
            </div>

            {/* Currency */}
            <div>
              <Label htmlFor="currency_iso_code">Product Currency</Label>
              <Select
                value={formData.currency_iso_code}
                onValueChange={(value) => handleChange('currency_iso_code', value)}
              >
                <SelectTrigger id="currency_iso_code" aria-describedby="currency_iso_code-help">
                  <SelectValue placeholder="Select currency" />
                </SelectTrigger>
                <SelectContent>
                  {currencyCodes.map((code) => (
                    <SelectItem key={code} value={code}>
                      {code}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p id="currency_iso_code-help" className="text-xs text-muted-foreground mt-1">
                Metadata on the product record. The price below has its own currency and is not
                affected by this.
              </p>
            </div>

            {/* SKU */}
            <div>
              <Label htmlFor="stock_keeping_unit">SKU</Label>
              <Input
                id="stock_keeping_unit"
                value={formData.stock_keeping_unit}
                onChange={(e) => handleChange('stock_keeping_unit', e.target.value)}
              />
            </div>

            {/* Unit of Measure */}
            <div>
              <Label htmlFor="quantity_unit_of_measure">Unit of Measure</Label>
              <Input
                id="quantity_unit_of_measure"
                value={formData.quantity_unit_of_measure}
                onChange={(e) => handleChange('quantity_unit_of_measure', e.target.value)}
                placeholder="e.g., Each, Box, Case"
              />
            </div>

            {/* Unit Price — stored as a pricebook entry in its own currency */}
            <div>
              <Label htmlFor="unit_price">
                Unit Price ({formData.pricing_currency}) (Optional)
              </Label>
              <div className="flex gap-2">
                <Input
                  id="unit_price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.unit_price}
                  onChange={(e) => handleChange('unit_price', e.target.value)}
                  placeholder="0.00"
                  aria-invalid={!!errors.unit_price}
                  aria-describedby={
                    errors.unit_price ? 'unit_price-error' : 'pricing_currency-help'
                  }
                />
                <Select
                  value={formData.pricing_currency}
                  onValueChange={(value) => handleChange('pricing_currency', value)}
                >
                  <SelectTrigger
                    id="pricing_currency"
                    className="w-28"
                    aria-label="Currency of the unit price"
                  >
                    <SelectValue placeholder="Currency" />
                  </SelectTrigger>
                  <SelectContent>
                    {currencyCodes.map((code) => (
                      <SelectItem key={code} value={code}>
                        {code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <p id="pricing_currency-help" className="text-xs text-muted-foreground mt-1">
                {product?.has_pricing
                  ? `Editing the ${formData.pricing_currency} price. Changing the currency moves this price to that currency.`
                  : 'No price set yet. Saving a price creates one in the selected currency.'}
              </p>
              {errors.unit_price && (
                <p id="unit_price-error" className="text-sm text-destructive mt-1" role="alert">
                  {errors.unit_price}
                </p>
              )}
              <p className="text-xs text-muted-foreground mt-1">
                Clear this field to remove pricing for this product
              </p>
            </div>

            {/* Active and Renewal Switches */}
            <div className="flex items-center gap-6">
              <div className="flex items-center space-x-2">
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => handleChange('is_active', checked)}
                />
                <Label htmlFor="is_active">Active</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="renewal"
                  checked={formData.renewal}
                  onCheckedChange={(checked) => handleChange('renewal', checked)}
                />
                <Label htmlFor="renewal">Renewal</Label>
              </div>
            </div>

            {/* Description */}
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                rows={4}
              />
            </div>

            {/* Form Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={updateMutation.isPending || updatePricingMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={updateMutation.isPending || updatePricingMutation.isPending}
              >
                {updateMutation.isPending || updatePricingMutation.isPending
                  ? 'Updating...'
                  : 'Update Product'}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
